import { journalEntriesMolecule } from '@web-app/atoms/in-session/journalEntriesMolecule';
import type { JournalEntry } from '@web-app/components/JournalTimeline/types';
import { useMolecule } from 'bunshi/react';
import { useAtomValue } from 'jotai';

export interface UseJournalEntriesResult {
  /** Journal entries for the current patient */
  entries: JournalEntry[];

  /** True if initial data is loading */
  isLoading: boolean;

  /** True if loading more entries (pagination) */
  isLoadingMore: boolean;

  /** True if more entries are available to load */
  hasMore: boolean;

  /** Error object if fetch failed, null otherwise */
  error: Error | null;

  /** Function to load next page of entries */
  loadMore: () => void;

  /** Function to refetch current data */
  refetch: () => void;

  /** Function to reset all state (useful when unmounting) */
  reset: () => void;
}

/**
 * Hook for fetching and managing journal entries for a patient.
 *
 * Handles pagination, loading states, and caching automatically.
 * Data is cached per patient ID for 5 minutes.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { entries, isLoading, hasMore, loadMore } = useJournalEntries();
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <JournalTimeline
 *       entries={entries}
 *       hasMore={hasMore}
 *       onLoadMore={loadMore}
 *     />
 *   );
 * }
 * ```
 */
export function useJournalEntries(): UseJournalEntriesResult {
  const { journalEntriesArrayAtom, journalEntriesQueryAtom } = useMolecule(
    journalEntriesMolecule,
  );

  const entries = useAtomValue(journalEntriesArrayAtom);
  const queryResult = useAtomValue(journalEntriesQueryAtom);

  const error = queryResult.error ?? null;

  return {
    entries,
    isLoading: queryResult.isPending,
    isLoadingMore: queryResult.isFetchingNextPage,
    hasMore: queryResult.hasNextPage ?? false,
    error,
    loadMore: queryResult.fetchNextPage,
    refetch: queryResult.refetch,
    reset: () => {
      // No-op for now, will be removed in later cleanup
    },
  };
}
